import type { ReactNode } from "react";
import { X } from "lucide-react";
import { createPortal } from "react-dom";
import { Toaster as Sonner, type ToasterProps } from "sonner";
import { Button } from "./button";
import { PopoverHeader, PopoverTitle, PopoverDescription } from "./popover";

type ToastAction = { label: string; onClick: () => void };

// Sonner supplies stacking, live announcements and dismissal. All notice chrome
// lives in this shared UI default, with the same header and buttons as popovers.
function ToastContent({
  icon,
  tone,
  title,
  description,
  action,
  cancel,
}: {
  icon: ReactNode;
  tone: "info" | "warning" | "error";
  title: string;
  description?: string;
  action?: ToastAction;
  cancel?: ToastAction;
}) {
  const toneText = {
    info: "text-primary",
    warning: "text-quality-warning",
    error: "text-destructive",
  };
  // Sonner's stock layout: actions sit on the right, centred on the body.
  return (
    <div
      data-slot="toast-content"
      className="flex w-full items-center gap-3 rounded-xl border border-popover-border bg-popover p-4 text-sm text-popover-foreground shadow-sm"
    >
      <div className="flex min-w-0 flex-1 items-start gap-3">
        {/* h-3.5 matches the title's line box, centring the icon on it. */}
        <span className={`flex h-3.5 shrink-0 items-center [&_svg]:size-4 ${toneText[tone]}`}>
          {icon}
        </span>
        <PopoverHeader className="min-w-0 flex-1">
          <PopoverTitle className="text-sm">{title}</PopoverTitle>
          {description ? <PopoverDescription>{description}</PopoverDescription> : null}
        </PopoverHeader>
      </div>
      {action || cancel ? (
        <div className="flex shrink-0 items-center gap-1">
          {action ? (
            <Button size="sm" onClick={action.onClick}>
              {action.label}
            </Button>
          ) : null}
          {cancel ? (
            <Button
              size="icon-sm"
              variant="ghost"
              aria-label={cancel.label}
              title={cancel.label}
              onClick={cancel.onClick}
            >
              <X aria-hidden="true" />
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function StageToaster(props: Partial<ToasterProps>) {
  return createPortal(
    <Sonner
      theme="dark"
      position="bottom-center"
      // Bespoke stage dock heights (112px / 72px) plus stock 12px / 16px gaps.
      offset={{ bottom: "124px", top: "16px" }}
      mobileOffset={{ bottom: "88px", top: "16px" }}
      className="toaster group font-sans"
      toastOptions={{ unstyled: true }}
      {...props}
    />,
    document.body,
  );
}

export { StageToaster, ToastContent };
